import { MapMode, MapPlace, Rgb } from './map-types';
import { SubRegionAction } from '../rules/suburb-subregion-edits';

/**
 * How the suburb map draws (#256): its colours, line widths, label zooms and marker icons, in one place. deck.gl takes
 * RGBA arrays, not CSS, so these can't be theme tokens; the map draws over a basemap that doesn't follow the theme
 * either, so they stay the same in light and dark. Region and sub-region colours do come from the tokens
 * (--app-region-n, --app-subregion-n): the workspace reads them and hands the engine RGB.
 */

type Rgba = [number, number, number, number];

const AMBER: Rgb = [245, 158, 11];
const SLATE: Rgb = [100, 116, 139];
const INK: Rgb = [15, 23, 42];
const WHITE: Rgb = [255, 255, 255];
const SKY: Rgb = [56, 189, 248];
const RED: Rgb = [248, 113, 113];

/** Suburb names appear from this zoom in; below it they would be an unreadable pile. */
export const LABEL_ZOOM = 10.5;
/** Place names show from this zoom (street level), so the dots stay readable further out. */
export const PLACE_LABEL_ZOOM = 14.5;
export const LABEL_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
export const LABEL_FONT_SETTINGS = { sdf: true, fontSize: 48, buffer: 6 };

/** A suburb's fill: grey when unassigned, else its region's colour, stronger when covered and strongest on hover. */
export const SUBURB_FILL = {
  unassigned: [...SLATE, 38] as Rgba,
  unassignedHover: [148, 163, 184, 110] as Rgba,
  regionFallback: SLATE,
  alpha: { hover: 235, covered: 200, assigned: 140 },
};

/** A suburb's outline: the selected one amber, the hovered one white, covered ones dark and strong. */
export const SUBURB_LINE = {
  selected: [...AMBER, 255] as Rgba,
  hover: [248, 250, 252, 255] as Rgba,
  covered: [...INK, 230] as Rgba,
  other: [148, 163, 184, 90] as Rgba,
  width: { selected: 3.5, hover: 2.5, covered: 2.2, other: 0.6 },
};

/** Sub-region outlines; the one being edited is thicker, with a light fill. */
export const SUBREGION_LINE = { alpha: 255, fillAlpha: 46, width: 2, activeWidth: 3.5 };

/** The white dot on a suburb with a website page. */
export const PAGE_DOT = {
  fill: [...WHITE, 240] as Rgba,
  line: [...INK, 255] as Rgba,
  radius: 4,
  lineWidth: 1.5,
};

export const SUBURB_LABEL = {
  colour: [248, 250, 252, 235] as Rgba,
  outline: [...INK, 220] as Rgba,
  size: 11,
  /** Below the page dot, where there is one, rather than on top of it. */
  belowDot: [0, 13] as [number, number],
};

/** Places in Conductor or on the website get icons; the rest are dots in their state's colour. */
export const PLACE_FILL: Record<MapPlace['state'], Rgb> = {
  both: [37, 99, 235],
  conductor: [37, 99, 235],
  website: [22, 163, 74],
  pending: AMBER,
  hidden: SLATE,
  other: WHITE,
};
export const ICON_STATES = new Set<MapPlace['state']>(['both', 'conductor', 'website']);

export const PLACE_DOT = {
  alpha: 250,
  radius: { pending: 7, other: 5, selectedExtra: 3 },
  line: {
    selected: [...AMBER, 255] as Rgba,
    other: [...INK, 255] as Rgba,
    light: [...WHITE, 255] as Rgba,
  },
  lineWidth: { selected: 3, other: 1.5 },
};
export const PLACE_ICON = {
  haloRadius: 17,
  halo: [...AMBER, 230] as Rgba,
  discSize: 27,
  conductorSize: 24,
  picalunaSize: 26,
  badgeDiscSize: 16,
  badgeSize: 15,
  badgeOffset: [11, -11] as [number, number],
};
export const PLACE_LABEL = {
  colour: [...INK, 255] as Rgba,
  outline: [...WHITE, 230] as Rgba,
  size: 11,
  offset: { icon: 14, dot: 9 },
};

/** The pin of a place being added or moved: amber with a white ring. */
export const PIN = {
  fill: [...AMBER, 255] as Rgba,
  line: [...WHITE, 255] as Rgba,
  radius: 9,
  lineWidth: 3,
};

const assetUrl = (path: string) => new URL(path, document.baseURI).href;

/**
 * In Conductor and on the website get the products' own icons (Ivan 2026-10-16): Conductor's favicon on a white disc,
 * Picaluna's favicon for the website, and Picaluna's with a small Conductor badge for both. The files live in
 * src/assets/images/map/ (Conductor's from src/assets/favicon.ico, Picaluna's from picaluna-v3's public/favicon.svg).
 */
export const ICON = {
  conductor: {
    id: 'conductor',
    url: assetUrl('assets/images/map/conductor-marker.png'),
    width: 256,
    height: 255,
    mask: false,
  },
  picaluna: {
    id: 'picaluna',
    url: assetUrl('assets/images/map/picaluna-marker.svg'),
    width: 128,
    height: 128,
    mask: false,
  },
  disc: {
    id: 'disc',
    url:
      'data:image/svg+xml;utf8,' +
      encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><circle cx="32" cy="32" r="31" fill="white" stroke="rgba(15,23,42,0.35)" stroke-width="2"/></svg>'
      ),
    width: 64,
    height: 64,
    mask: false,
  },
};

/** The lasso's line: red when it takes suburbs out, else the colour of what it puts them in. */
export const LASSO = { width: 2, dash: [6, 4], cornerRadius: 3 };

export function lassoColour(
  mode: MapMode,
  action: SubRegionAction,
  subRegionRgb: Rgb | undefined,
  regionRgb: Rgb | undefined
): Rgb {
  if (mode === 'erase' || (mode === 'subregion' && action === 'remove')) return RED;
  if (mode === 'subregion') return subRegionRgb ?? SKY;
  return regionRgb ?? SKY;
}
